// src/components/DashboardNavbar.js
import { DashboardNavbarHtml } from './skins/dashboard-navbar.ts';
import { globalState } from "../framework/globalState.ts";
import {
  fetchPvAssetSummary,
  fetchBatteryAssetSummary,
  fetchBatteryAsset,
  fetchGeneratorAssetSummary,
  fetchGridAssetSummary,
  fetchWeather,
} from "../services/api.ts";
import { subscribeDeviceState, isMqttRealtimeEnabled } from "../services/mqttClient.ts";
import {
  applySummaryThresholdToElement,
  clearThresholdIndicator,
  type SummaryThresholdSpec,
} from "../services/summaryThreshold.ts";
import { formatSiteDate, formatSiteTime, resolveSiteTimezone } from "../utils/timezone.ts";

const TOPBAR_THRESHOLD_SPEC: SummaryThresholdSpec = {
  'PV Power': { keys: ['INV_ACTIVE_POWER', 'INV_ACTIVE_POWER_AC'], mode: 'sum' },
  'PV Reactive Power': { keys: ['INV_REACTIVE_POWER'], mode: 'sum' },
  'PV Total Energy': { keys: ['INV_ENERGY_TOTAL', 'INV_TOTAL_ENERGY'], mode: 'sum' },

  'BESS Power': { keys: ['BESS_ACTIVE_POWER'], mode: 'sum' },
  'BESS SOC': { keys: ['BESS_SOC'], mode: 'avg' },
  'BESS Total Energy': {
    keys: ['BESS_CHARGE_ENERGY', 'BESS_DISCHARGE_ENERGY', 'BESS_CHARGE_ENERGY_TODAY', 'BESS_DISCHARGE_ENERGY_TODAY'],
    mode: 'sum',
  },

  'Gen Power': { keys: ['DG_ACTIVE_POWER'], mode: 'sum' },
  'Gen Fuel Level': { keys: ['DG_FUEL_LEVEL'], mode: 'avg' },

  'MET Active Power': { keys: ['GRID_ACTIVE_POWER'], mode: 'sum' },
  'MET Reactive Power': { keys: ['GRID_REACTIVE_POWER'], mode: 'sum' },
  'MET Total Energy': { keys: ['GRID_ENERGY_IMPORT', 'GRID_IMPORT_ENERGY'], mode: 'priorityAvg' },

  'Plant Irradiance': { keys: ['MET_POA_IRRADIANCE', 'MET_GHI', 'MET_IRRADIANCE'], mode: 'priorityAvg' },
  'Ambient Temp': { keys: ['MET_AMBIENT_TEMP'], mode: 'avg' },
  'Wind Speed': { keys: ['MET_WIND_SPEED'], mode: 'avg' },

  'Grid Power': { keys: ['GRID_ACTIVE_POWER'], mode: 'sum' },
};

export default function DashboardNavbar() {
  const wrapper = document.createElement('div');
  const dashboardNavbar = new DashboardNavbarHtml();
  const html = dashboardNavbar.dashboardnavbarskin();
  wrapper.innerHTML = html.trim();
  const navbar = wrapper.firstElementChild as HTMLElement;

  // ———————————————————— SITE CONTEXT ————————————————————
  const siteWithData = globalState.currentSiteWithData;
  const site = ((siteWithData as any)?.site || siteWithData) as { _id?: string } | null;
  const siteId = site?._id;
  const siteTimezone = resolveSiteTimezone(site);
  const devices = ((siteWithData as any)?.devices || []) as Array<{ registers?: Array<Record<string, any>> }>;
  const siteRegisters = devices.flatMap((d) => d.registers || []);
  const registerById = new Map<string, any>();
  siteRegisters.forEach((reg: any) => {
    if (!reg?._id) return;
    registerById.set(String(reg._id), reg);
  });

  // ———————————————————— BASIC INFO ————————————————————
  const dateEl = navbar.querySelector("#date") as HTMLElement | null;
  const timeEl = navbar.querySelector("#time") as HTMLElement | null;
  const usernameEl = navbar.querySelector("#username") as HTMLElement | null;

  const updateClock = () => {
    const now = new Date();
    if (dateEl) dateEl.textContent = formatSiteDate(now, siteTimezone);
    if (timeEl) timeEl.textContent = formatSiteTime(now, siteTimezone);
  };

  updateClock();

  const clockInterval = setInterval(() => {
    if (!navbar.isConnected) {
      clearInterval(clockInterval);
      return;
    }
    updateClock();
  }, 1000);
  if (usernameEl) usernameEl.textContent = (globalState.user as any)?.username || "Guest";

  const headingEl = navbar.querySelector(".Page.heading");
  const pageTitle = getPageTitle(window.location.pathname);
  if (headingEl) headingEl.textContent = pageTitle;

  if (!siteId) return navbar;

  const setText = (
    field: string,
    value: unknown,
    opts: { digits?: number; asStatus?: boolean; applyThreshold?: boolean } = {},
  ) => {
    const span = navbar.querySelector(`[data-register="${field}"]`) as HTMLElement | null;
    if (!span) return;

    if (opts.asStatus) {
      const text = String(value ?? '').trim();
      span.textContent = text || 'Unknown';
      clearThresholdIndicator(span);
      return;
    }

    const digits = opts.digits ?? 1;
    span.textContent = formatValue(value, digits);

    if (opts.applyThreshold !== false) {
      applySummaryThresholdToElement(span, field, value, siteRegisters, TOPBAR_THRESHOLD_SPEC);
    } else {
      clearThresholdIndicator(span);
    }
  };

  const setFailModules = (failedCount: number | null) => {
    const span = navbar.querySelector('[data-register="Fail Modules"]') as HTMLElement | null;
    if (!span) return;

    const failed = Number.isFinite(failedCount as number) ? Number(failedCount) : null;
    span.textContent = failed == null ? '—' : String(Math.max(0, Math.round(failed)));

    if (failed == null) {
      span.style.backgroundColor = '';
      span.style.color = '';
      span.title = '';
      return;
    }

    span.style.backgroundColor = failed > 0 ? '#d32f2f' : '#2e7d32';
    span.style.color = '#ffffff';
    span.title = failed > 0
      ? `${failed} inverter(s) offline or in fault`
      : 'All inverters running';
  };

  const aggregateRegisterKeys = (keys: string[], mode: 'sum' | 'avg' | 'priorityAvg') => {
    if (!Array.isArray(keys) || keys.length === 0) return null;

    const collectForKey = (key: string): number[] => {
      return siteRegisters
        .filter((reg: any) => String(reg?.dictionaryKey || '') === key)
        .map((reg: any) => Number(reg?.value))
        .filter((value: number) => Number.isFinite(value));
    };

    if (mode === 'priorityAvg') {
      for (const key of keys) {
        const values = collectForKey(key);
        if (!values.length) continue;
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        return Number.isFinite(avg) ? avg : null;
      }
      return null;
    }

    const values = keys.flatMap((key) => collectForKey(key));
    if (!values.length) return null;

    if (mode === 'avg') {
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      return Number.isFinite(avg) ? avg : null;
    }

    const sum = values.reduce((a, b) => a + b, 0);
    return Number.isFinite(sum) ? sum : null;
  };

  const updateFromRegisterSnapshot = () => {
    setText('PV Power', aggregateRegisterKeys(['INV_ACTIVE_POWER', 'INV_ACTIVE_POWER_AC'], 'sum'));
    setText('PV Reactive Power', aggregateRegisterKeys(['INV_REACTIVE_POWER'], 'sum'));
    setText('PV Total Energy', aggregateRegisterKeys(['INV_ENERGY_TOTAL', 'INV_TOTAL_ENERGY'], 'sum'));

    setText('BESS Power', aggregateRegisterKeys(['BESS_ACTIVE_POWER'], 'sum'));
    setText('BESS SOC', aggregateRegisterKeys(['BESS_SOC'], 'avg'));
    setText('BESS Total Energy', aggregateRegisterKeys([
      'BESS_CHARGE_ENERGY',
      'BESS_DISCHARGE_ENERGY',
      'BESS_CHARGE_ENERGY_TODAY',
      'BESS_DISCHARGE_ENERGY_TODAY',
    ], 'sum'));

    setText('Gen Power', aggregateRegisterKeys(['DG_ACTIVE_POWER'], 'sum'));
    setText('Gen Fuel Level', aggregateRegisterKeys(['DG_FUEL_LEVEL'], 'avg'));

    setText('MET Active Power', aggregateRegisterKeys(['GRID_ACTIVE_POWER'], 'sum'));
    setText('MET Reactive Power', aggregateRegisterKeys(['GRID_REACTIVE_POWER'], 'sum'));
    setText('MET Total Energy', aggregateRegisterKeys(['GRID_ENERGY_IMPORT', 'GRID_IMPORT_ENERGY'], 'priorityAvg'));

    setText('Plant Irradiance', aggregateRegisterKeys(['MET_POA_IRRADIANCE', 'MET_GHI', 'MET_IRRADIANCE'], 'priorityAvg'));
    setText('Ambient Temp', aggregateRegisterKeys(['MET_AMBIENT_TEMP'], 'avg'));
    setText('Wind Speed', aggregateRegisterKeys(['MET_WIND_SPEED'], 'avg'));

    setText('Grid Power', aggregateRegisterKeys(['GRID_ACTIVE_POWER'], 'sum'));
    setText('Avail Power P', aggregateRegisterKeys(['KPI_PV_AVAILABILITY_PCT_24H', 'PV_AVAILABILITY', 'INV_AVAILABILITY'], 'priorityAvg'), { applyThreshold: false });
  };

  const toStatus = (isOnline: unknown): string => {
    if (isOnline === true) return 'Running';
    if (isOnline === false) return 'Stopped';
    return 'Unknown';
  };

  const sumAssetRegisterValues = (assetRows: any, keys: string[]) => {
    if (!Array.isArray(assetRows) || keys.length === 0) return null;
    const keySet = new Set(keys);

    let total = 0;
    let found = false;

    for (const row of assetRows) {
      const registers = row?.registers;
      if (!registers || typeof registers !== 'object') continue;

      for (const [key, reg] of Object.entries(registers as Record<string, any>)) {
        if (!keySet.has(key)) continue;
        const value = Number(reg?.value);
        if (!Number.isFinite(value)) continue;
        total += value;
        found = true;
      }
    }

    return found ? total : null;
  };

  const updateFromAssets = async () => {
    try {
      // Sequential loads to avoid 429 rate-limiting bursts
      const pv = await fetchPvAssetSummary(siteId) as any;
      const battery = await fetchBatteryAssetSummary(siteId) as any;
      const batteryAssetRows = await fetchBatteryAsset(siteId) as any;
      const generator = await fetchGeneratorAssetSummary(siteId) as any;
      const grid = await fetchGridAssetSummary(siteId) as any;
      const weather = await fetchWeather(siteId) as any;

      const bessTotalEnergy = sumAssetRegisterValues(batteryAssetRows, [
        'BESS_CHARGE_ENERGY',
        'BESS_DISCHARGE_ENERGY',
        'BESS_CHARGE_ENERGY_TODAY',
        'BESS_DISCHARGE_ENERGY_TODAY',
      ]);

      // INVERTERS
      setText('PV Power', pv?.activePower);
      setText('PV Reactive Power', pv?.reactivePower);
      setText('PV Total Energy', pv?.totalEnergy);

      // BESS
      setText('BESS Power', battery?.batteryPower);
      setText('BESS SOC', battery?.soc);
      setText('BESS Total Energy', bessTotalEnergy);

      // GENERATOR
      setText('Gen Power', generator?.activePower);
      setText('Gen Fuel Level', generator?.fuelLevel);
      setText('Gen Status', toStatus(generator?.operatingStatus?.isOnline), { asStatus: true, applyThreshold: false });

      // METERS / GRID
      setText('MET Active Power', grid?.activePower);
      setText('MET Reactive Power', grid?.reactivePower);
      setText('MET Total Energy', grid?.importEnergy);

      // MET STATION (prefer local sensor registers, fall back to weather API values)
      setText('Plant Irradiance',
        weather?.local?.poa?.value
        ?? weather?.local?.ghi?.value
        ?? weather?.fallback?.poa
        ?? weather?.fallback?.ghi
        ?? weather?.fallback?.irradiance);
      setText('Ambient Temp', weather?.local?.temperature?.value ?? weather?.fallback?.temperature);
      setText('Wind Speed', weather?.local?.windSpeed?.value ?? weather?.fallback?.windSpeed);

      // AVAILABLE POWER
      setFailModules(pv?.operatingStatus?.faultCount ?? null);
      setText('Grid Power', grid?.activePower);
      setText('Avail Power P', pv?.inverterAvailability?.percent, { applyThreshold: false });
    } catch (err) {
      console.warn('Failed to load dashboard topbar asset data:', err);
    }
  };

  if (!isMqttRealtimeEnabled()) {
    void updateFromAssets();
  }
  updateFromRegisterSnapshot();

  const unsubscribeDeviceState = subscribeDeviceState(siteId, (payload: any) => {
    const readings = Array.isArray(payload?.readings) ? payload.readings : [];
    if (!readings.length) return;

    let changed = false;
    readings.forEach((reading: any) => {
      const registerId = String(reading?.registerId || '');
      if (!registerId) return;
      const reg = registerById.get(registerId);
      if (!reg) return;

      const nextValue = Number(reading?.value);
      if (!Number.isFinite(nextValue)) return;

      reg.value = nextValue;
      reg.updatedAt = reading?.timestamp || new Date().toISOString();
      changed = true;
    });

    if (changed) {
      updateFromRegisterSnapshot();
    }
  });

  const poll = setInterval(() => {
    if (!navbar.isConnected) {
      try {
        unsubscribeDeviceState?.();
      } catch {
        // ignore cleanup errors
      }
      clearInterval(poll);
      return;
    }

    // API is fallback only when MQTT is off or disconnected.
    if (isMqttRealtimeEnabled()) {
      return;
    }

    void updateFromAssets();
  }, 20_000);

  return navbar;
}

// ———————————————————— HELPERS ————————————————————
function formatValue(value: unknown, digits = 1) {
  if (value == null) return "—";

  const num = Number(value);
  if (isNaN(num)) return "—";
  return num.toFixed(digits);
}

function getPageTitle(path: string) {
  const map = {
    "/dashboard": "Overview",
    "/dashboard/pv": "PV",
    "/dashboard/bess": "BESS",
    "/dashboard/storage": "Generator",
    "/dashboard/schematics": "Schematics",
    "/dashboard/met-station": "Met Stations",
    "/dashboard/grid": "Grid Controllers",
    "/dashboard/carbon-credit": "Carbon Credits",
    "/dashboard/communication": "Communication",
    "/dashboard/data-table": "Data Table",
    "/dashboard/alarms": "Alarms",
    "/dashboard/historical": "Historical",
    "/dashboard/heatmap": "Heat Map",
    "/dashboard/power-control": "Power Control",
    "/dashboard/documents": "Documents",
    "/dashboard/event-viewer": "Event Viewer",
    "/dashboard/user-management": "User Management",
    "/dashboard/reports": "Reports"
  };
  return map[path.replace(/\/$/, '')] || "Overview";
}